import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import {
  getRequirements,
  updateRequirement,
} from "sr/utils/api/corporate/requirements";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { ModelWrapperWithButton } from "../layout/ModelWrapperWithButton";
import { ReviewDataEdit } from "../components/ReviewDataEdit";
import { RequirementCommonHeader } from "../layout/RequirementCommonHeader";
import { Button } from "sr/helpers/ui-components/company/Button";
import { uploadFile } from "sr/utils/api/uploadFile";
import { RequirementReviewSchema } from "sr/utils/schemas/corporate/requirements/RequirementReviewSchema";
import RequirementTitleChange from "../components/modals/RequirementTitleChange";
import RequirementDescriptionChange from "../components/modals/RequirementDescriptionChange";
import RequirementBusinessTypesChange from "../components/modals/RequirementBusinessTypesChange";
import RequirementServiceTypesChange from "../components/modals/RequirementServiceTypesChange";
import RequirementBudgetChange from "../components/modals/RequirementBudgetChange";
import RequirementStatusChange from "../components/modals/RequirementStatusChange";
import RequirementAddressChange from "../components/modals/RequirementAddressChange";
import RequirementAreaChange from "../components/modals/RequirementAreaChange";
import RequirementAreaTypeChange from "../components/modals/RequirementAreaTypeChange";
import RequirementFrequencyChange from "../components/modals/RequirementFrequencyChange";
import RequirementDurationChange from "../components/modals/RequirementDurationChange";
import RequirementStartDateChange from "../components/modals/RequirementStartDateChange";
import RequirementEndDateChange from "../components/modals/RequirementEndDateChange";
import RequirementSpecialInstructionsChange from "../components/modals/RequirementSpecialInstructionsChange";
import RequirementDocumentsChange from "../components/modals/RequirementDocumentsChange";
import DocumentUpload from "sr/helpers/ui-components/DocumentUpload";
import { getBusinessType } from 'sr/utils/api/masterData/getBusinessType'
import { getAreaType } from 'sr/utils/api/masterData/getAreaType'
import { getFrequency } from 'sr/utils/api/masterData/getFrequency'
import { getDuration } from 'sr/utils/api/masterData/getDuration'
import { serviceTypes as serviceTypeStatic } from '../requirement-post/RequirementJsonData'


export const RequirementReview = () => {
  const intl = useIntl();
  const { reqId } = useParams();
  const corporateData = useSelector((state: any) => state.corporate?.data);

  const [loader, setLoader] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);

  const [requirement, setRequirement] = useState<any>(null);
  const [businessTypeOptions, setBusinessTypeOptions] = useState<any[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<any[]>([]);
  const [areaTypeOptions, setAreaTypeOptions] = useState<any[]>([]);
  const [frequencyOptions, setFrequencyOptions] = useState<any[]>([]);
  const [durationOptions, setDurationOptions] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [activeComponent, setActiveComponent] = useState<any>({});
  const childRef = useRef<any>(null);
  const [modalErrorId, setModalErrorId] = useState<string>("");
  const [modalErrorPath, setModalErrorPath] = useState<string>("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);
      try {
        const res = await getRequirements({ corporate_id: corporateData?.id });
        if (res?.success) {
          const match = res.data.find((r: any) => r.id === reqId);
          if (match) {
            setRequirement(match);
          } else {
            toast.error(intl.formatMessage({ id: 'API.ERROR.REQUIREMENT.NOT_FOUND', defaultMessage: 'Requirement not found' }));
          }
        }
        try {
          const bt = await getBusinessType();
          if (bt?.success && Array.isArray(bt.data)) {
            setBusinessTypeOptions(bt.data.map((i:any)=> ({ value:i.value, label:i.label })));
          }
        } catch {}
        setServiceTypeOptions(serviceTypeStatic.map((s:any)=> ({ value:s.value, label:s.label })));
        try {
          const a = await getAreaType();
          if (a?.success && Array.isArray(a.data)) {
            setAreaTypeOptions(a.data.map((i:any)=> ({ value:i.value, label:i.label })));
          }
        } catch {}
        try {
          const f = await getFrequency();
          if (f?.success && Array.isArray(f.data)) {
            setFrequencyOptions(f.data.map((i:any)=> ({ value:i.value, label:i.label })));
          }
        } catch {}
        try {
          const d = await getDuration();
          if (d?.success && Array.isArray(d.data)) {
            setDurationOptions(d.data.map((i:any)=> ({ value:i.value, label:i.label })));
          }
        } catch {}
      } catch (err) {
        toast.error(intl.formatMessage({ id: 'API.ERROR.REQUIREMENT.FETCH', defaultMessage: 'Failed to fetch requirements' }));
      }
      setLoader(false);
    };

    if (corporateData?.id && reqId) {
      fetchData();
    }
  }, [corporateData?.id, reqId]);

  const AllModels = [
    { label: "Title", componentName: RequirementTitleChange },
    { label: "Description", componentName: RequirementDescriptionChange },
    { label: "Business Types", componentName: RequirementBusinessTypesChange },
    { label: "Service Types", componentName: RequirementServiceTypesChange },
    { label: "Budget", componentName: RequirementBudgetChange },
    { label: "Status", componentName: RequirementStatusChange },
    { label: "Address", componentName: RequirementAddressChange },
    { label: "Area", componentName: RequirementAreaChange },
    { label: "Area Type", componentName: RequirementAreaTypeChange },
    { label: "Frequency", componentName: RequirementFrequencyChange },
    { label: "Duration", componentName: RequirementDurationChange },
    { label: "Start Date", componentName: RequirementStartDateChange },
    { label: "End Date", componentName: RequirementEndDateChange },
    { label: "Special Instructions", componentName: RequirementSpecialInstructionsChange },
    { label: "Documents", componentName: RequirementDocumentsChange },
  ];

  const formatAddress = (address?: any) => {
    if (!address) return "";
    return [
      address.address_line_1,
      address.address_line_2,
      address.city,
      address.state,
      address.country,
      address.postal,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const openModel = (index: number) => {
    setActiveComponent(AllModels[index]);
    setShowModal(true);
    setModalErrorId("");
    setModalErrorPath("");
  };

  const HandleSave = async () => {
    if (!childRef.current?.submitForm) return;
    if (typeof childRef.current.validateData === 'function') {
      const ok = await childRef.current.validateData();
      if (!ok) {
        setModalErrorId("");
        setModalErrorPath("");
        return;
      }
    }
    const newData = await childRef.current.submitForm();
    const payload = { ...requirement, ...newData };

    setLoader(true);
    try {
      const result = await updateRequirement(requirement.id, payload);
      if (result?.success) {
        setRequirement(payload);
        setShowModal(false);
      }
    } catch (e) {
      setModalErrorId('API.ERROR.REQUIREMENT.UPDATE');
    } finally {
      setLoader(false);
    }
  };

  const validateAndContinue = async () => {
    try {
      await RequirementReviewSchema.validate(requirement, { abortEarly: false });
      navigate("/corporate/requirement-listing");
    } catch (err: any) {
      const firstMsg = err?.errors?.[0] || 'INPUT.VALIDATION.REQUIREMENT.GENERIC';
      const path: string = err?.inner?.[0]?.path || err?.path;
      if (path) {
        const segments = String(path).split('.')
        const key = segments[0];
        const map: Record<string, number> = {
          title: 0,
          description: 1,
          business_types: 2,
          service_type: 3,
          budget: 4,
          status: 5,
          address: 6,
          area: 7,
          area_type: 8,
          frequency: 9,
          duration: 10,
          start_date: 11,
          end_date: 12,
          special_instructions: 13,
          documents_required: 14,
        };
        if (map[key] !== undefined) {
          setActiveComponent(AllModels[map[key]]);
          setShowModal(true);
          setModalErrorId(firstMsg);
          setModalErrorPath(path);
        }
      }
    }
  };

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !reqId) return;

    setUploading(true);
    try {
      const uploaded: any[] = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("task_img", file);
        const res = await uploadFile(formData);
        if (res?.success) {
          const key = res.data?.files?.[0]?.key;
          if (key) {
            uploaded.push({ url: key, name: file.name });
          }
        }
      }

      const updatedAttachments = [...(requirement.attachments || []), ...uploaded];
      const payload = { ...requirement, attachments: updatedAttachments };
      const result = await updateRequirement(reqId, payload);

      if (result?.success) {
        setRequirement(payload);
      }
    } catch {
      toast.error(intl.formatMessage({ id: 'API.ERROR.REQUIREMENT.UPLOAD', defaultMessage: 'Failed to upload files' }));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAttachmentRemove = async (index: number) => {
    if (!reqId) return;
    setRemovingIndex(index);

    const updated = (requirement.attachments || []).filter((_: any, i: number) => i !== index);
    const payload = { ...requirement, attachments: updated };

    try {
      const result = await updateRequirement(reqId, payload);
      if (result?.success) {
        setRequirement(payload);
      }
    } catch {
      toast.error(intl.formatMessage({ id: 'API.ERROR.REQUIREMENT.REMOVE_FILE', defaultMessage: 'Failed to remove file' }));
    } finally {
      setRemovingIndex(null);
    }
  };

  return (
    <div>
      <ContentWithSpinner loader={loader} height="h-screen">
        {requirement && (
          <div className="container mx-auto max-w-6xl py-8 px-4">
            <RequirementCommonHeader
              title={
                <FormattedMessage
                  id="CORPORATE.REQUIREMENT.POST.TITLE"
                  defaultMessage="Review Requirement"
                />
              }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <ReviewDataEdit title="Title" value={requirement.title} onClick={() => openModel(0)} />
              <ReviewDataEdit title="Status" value={requirement.status} onClick={() => openModel(5)} />

              <div className="md:col-span-2">
                <ReviewDataEdit
                  title="Description"
                  value={requirement.description}
                  onClick={() => openModel(1)}
                />
              </div>

              <ReviewDataEdit title="Business Types" multivalue={requirement.business_types} options={businessTypeOptions} onClick={() => openModel(2)} />
              <ReviewDataEdit title="Service Types" multivalue={requirement.service_type} options={serviceTypeOptions} onClick={() => openModel(3)} />
              <ReviewDataEdit title="Budget" value={requirement.budget} onClick={() => openModel(4)} />
              <ReviewDataEdit
                title="Address"
                value={formatAddress(requirement.address)} 
                onClick={() => openModel(6)}
              />

              <ReviewDataEdit title="Area" value={requirement.area} onClick={() => openModel(7)} />
              <ReviewDataEdit title="Area Type" value={requirement.area_type} options={areaTypeOptions} onClick={() => openModel(8)} />
              <ReviewDataEdit title="Frequency" value={requirement.frequency} options={frequencyOptions} onClick={() => openModel(9)} />
              <ReviewDataEdit title="Duration" value={requirement.duration} options={durationOptions} onClick={() => openModel(10)} />
              <ReviewDataEdit
                title="Start Date"
                value={requirement.start_date ? new Date(requirement.start_date).toLocaleDateString() : ''}
                onClick={() => openModel(11)}
              />
              <ReviewDataEdit
                title="Special Instructions"
                value={requirement.special_instructions}
                onClick={() => openModel(13)}
              />
              <ReviewDataEdit
                title="Documents Required"
                multivalue={requirement.documents_required?.map((d: any) => d.doc_name)}
                onClick={() => openModel(14)}
              />

              <DocumentUpload
                documents={(requirement.attachments || []).map((a: any, i: number) => ({
                  url: a.url,
                  name: a.name || (typeof a.url === 'string' ? (a.url.split('/').pop() || `Document ${i+1}`) : `Document ${i+1}`),
                }))}
                onUpload={handleAttachmentUpload}
                onRemove={handleAttachmentRemove}
                uploading={uploading}
                removingIndex={removingIndex}
              />
            </div>

            <div className="flex justify-end mt-4">
              <Button
                text={
                  <FormattedMessage
                    id="RECRUITER.POSTJOB.CONTINUE.BUTTON"
                    defaultMessage="Continue"
                  />
                }
                onClick={validateAndContinue}
              />
            </div>
          </div>
        )}
      </ContentWithSpinner>

      <ModelWrapperWithButton
        Save={HandleSave}
        showModal={showModal}
        onCheckModel={setShowModal}
        headertitle={<FormattedMessage id="CORPORATE.REQUIREMENT.REVIEW.EDIT.TITLE" defaultMessage="Edit Requirement" />}
      >
        {activeComponent.componentName && (
          <activeComponent.componentName
            data={requirement}
            externalErrorId={modalErrorId}
            externalErrorPath={modalErrorPath}
            HandleDataProps={() => {}}
            ref={childRef}
          />
        )}
      </ModelWrapperWithButton>
    </div>
  );
};

export default RequirementReview;
